import React from 'react';
import { AlertCircle, AlertTriangle, XCircle, Zap, Info } from 'lucide-react';

export type SeverityLevel = 'critical' | 'high' | 'medium' | 'low' | 'info';

interface SeverityChipProps {
  severity: SeverityLevel;
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
  className?: string;
}

const SEVERITY_CONFIG = {
  critical: {
    label: 'Critical',
    icon: Zap,
    bgColor: 'bg-red-600',
    textColor: 'text-white',
    borderColor: 'border-red-600',
  },
  high: {
    label: 'High',
    icon: XCircle,
    bgColor: 'bg-red-500',
    textColor: 'text-white',
    borderColor: 'border-red-500',
  },
  medium: {
    label: 'Medium',
    icon: AlertTriangle,
    bgColor: 'bg-yellow-500',
    textColor: 'text-white',
    borderColor: 'border-yellow-500',
  },
  low: {
    label: 'Low',
    icon: AlertCircle,
    bgColor: 'bg-blue-500',
    textColor: 'text-white',
    borderColor: 'border-blue-500',
  },
  info: {
    label: 'Info',
    icon: Info,
    bgColor: 'bg-gray-500',
    textColor: 'text-white',
    borderColor: 'border-gray-500',
  },
} as const;

const SIZE_CONFIG = {
  sm: {
    padding: 'px-1.5 py-0.5',
    text: 'text-xs',
    iconSize: 'w-3 h-3',
  },
  md: {
    padding: 'px-2 py-1',
    text: 'text-sm',
    iconSize: 'w-4 h-4',
  },
  lg: {
    padding: 'px-3 py-1.5',
    text: 'text-base',
    iconSize: 'w-5 h-5',
  },
} as const;

export const SeverityChip: React.FC<SeverityChipProps> = ({
  severity,
  size = 'sm',
  showIcon = true,
  className = '',
}) => {
  const config = SEVERITY_CONFIG[severity];
  const sizeConfig = SIZE_CONFIG[size];
  const Icon = config.icon;

  return (
    <span
      className={`
        inline-flex items-center gap-1 rounded-full font-medium border
        ${config.bgColor} ${config.textColor} ${config.borderColor}
        ${sizeConfig.padding} ${sizeConfig.text}
        ${className}
      `}
      title={`${config.label} severity`}
    >
      {showIcon && <Icon className={sizeConfig.iconSize} />}
      {config.label}
    </span>
  );
}; 